import { Button, Checkbox, Text } from '@fluentui/react-components';
import { AddCircleRegular, Dismiss12Regular } from '@fluentui/react-icons';
import { observer } from 'mobx-react-lite';
import React, { useMemo, useState } from 'react';
import { QuestionnaireMediaType } from '../../../common/types/questionnaire-media-type';
import { FileUploadConfiguration, MAX_UPLOADED_MEDIA_COUNT, questionnaireImageFileConfiguration, questionnaireLogoFileConfiguration, questionnaireVideoFileConfiguration } from '../../../configurations';
import { Questionnaire, UploadedMedia } from '../../../models';
import { fileUploadValidator } from '../../../shared/functions';
import { CommonLabelAndValueList, UploadButton } from '../../shared-components';
import { FlexBox } from '../../shared-components/flex-box';

interface WinnerDetailsUploadedMediaProps {
  questionnaire: Questionnaire;
  logo?: UploadedMedia;
  media: UploadedMedia[];
  isEditMode: boolean;
  onDownloadUploadedMedia(uploadedMediaId: number): void;
  onDeleteUploadedMedia(uploadedMediaId: number): void;
  onUploadMedia(mediaContent: File, mediaType: QuestionnaireMediaType): void;
  onRestoreMedia(mediaType: QuestionnaireMediaType): void;
}

const WinnerDetailsUploadedMediaImpl: React.FC<WinnerDetailsUploadedMediaProps> = (props) => {
  const {
    questionnaire,
    logo,
    media,
    isEditMode,
    onDownloadUploadedMedia,
    onDeleteUploadedMedia,
    onRestoreMedia,
    onUploadMedia,
  } = props;

  const [uploadState, setUploadState] = useState<{
    errorMessages: Record<QuestionnaireMediaType, string[]>;
    fileInputKeys: Record<QuestionnaireMediaType, string>;
  }>({
    errorMessages: { logo: [], image: [], video: [] },
    fileInputKeys: { logo: '', image: '', video: '' },
  });

  const fileConfigurations: Record<QuestionnaireMediaType, FileUploadConfiguration> = {
    logo: questionnaireLogoFileConfiguration,
    image: questionnaireImageFileConfiguration,
    video: questionnaireVideoFileConfiguration,
  };

  const handleUploadedFile = (files: FileList, mediaType: QuestionnaireMediaType) => {
    setUploadState(prev => ({
      ...prev,
      errorMessages: {
        ...prev.errorMessages,
        [mediaType]: [],
      },
    }));

    if (!files?.[0] || files.length > 1) return;

    const file = files[0];

    setUploadState(prev => ({
      ...prev,
      fileInputKeys: {
        ...prev.fileInputKeys,
        [mediaType]: file.name,
      },
    }));

    const validationResult = fileUploadValidator(file, fileConfigurations[mediaType]);
    if (!validationResult.isValid) {
      setUploadState(prev => ({
        ...prev,
        errorMessages: {
          ...prev.errorMessages,
          [mediaType]: validationResult.errorMessages,
        },
      }));
      return;
    }

    onUploadMedia(file, mediaType);
  };

  const renderUploadedFile = (uploadedMedia: UploadedMedia, onDelete?: () => void) => (
    <FlexBox aligns='center'>
      <button
        onClick={() => onDownloadUploadedMedia(uploadedMedia.uploadedMediaId)}
        className='button-as-link'
      >
        {uploadedMedia.formattedFileName}
      </button>
      {
        onDelete &&
        <Button
          appearance='transparent'
          title='Remove'
          icon={<Dismiss12Regular />}
          onClick={onDelete}
        />
      }
    </FlexBox>
  );

  const renderUploaderForm = (mediaType: QuestionnaireMediaType, uploadedMedia: UploadedMedia[], disabled?: boolean) => (
    <UploadButton
      onUpload={(fileList) => fileList ? handleUploadedFile(fileList, mediaType) : null}
      acceptFiles={fileConfigurations[mediaType].supportedFileExtensions.join(',')}
      restoreButtonProps={{ onClick: () => onRestoreMedia(mediaType), hidden: !isEditMode }}
      validationMessage={uploadState.errorMessages[mediaType]?.at(0)}
      buttonProps={{
        children: 'Upload replacement',
        icon: <AddCircleRegular />,
        appearance: 'primary',
        disabled,
        hidden: !isEditMode,
      }}
    >
      {
        uploadedMedia.map((media) => (
          <React.Fragment key={media.uploadedMediaId}>
            {renderUploadedFile(media, isEditMode ? () => onDeleteUploadedMedia(media.uploadedMediaId) : undefined)}
          </React.Fragment>
        ))
      }
    </UploadButton>
  );

  const uploadedFilesForMediaForm = (media: UploadedMedia[], mediaType: QuestionnaireMediaType) => {
    return media.filter(item => (
      fileConfigurations[mediaType].supportedFileExtensions.some(ext => item.originalFileName.toLowerCase().endsWith(ext))
    ));
  };

  const renderImagesMedia = useMemo(() => uploadedFilesForMediaForm(media, 'image'), [JSON.stringify(media)]);
  const renderVideoMedia = useMemo(() => uploadedFilesForMediaForm(media, 'video'), [JSON.stringify(media)]);

  return (
    <>
      <FlexBox
        isFullWidth
        direction='column'
        className='winner-finalists-details__summary'
      >
        <Text
          weight='semibold'
          size={400}
        >
          Upload logo
        </Text>
        <CommonLabelAndValueList
          label='Logo:'
        >
          {renderUploaderForm('logo', (logo && logo.uploadedMediaId > 0) ? [logo] : [], logo && logo.uploadedMediaId > 0)}
        </CommonLabelAndValueList>
      </FlexBox>

      <FlexBox
        isFullWidth
        direction='column'
        className='winner-finalists-details__summary'
      >
        <Text
          weight='semibold'
          size={400}
        >
          Upload media
        </Text>
        <CommonLabelAndValueList
          label='Images:'
        >
          {renderUploaderForm('image', renderImagesMedia, media.length >= MAX_UPLOADED_MEDIA_COUNT)}
        </CommonLabelAndValueList>
        <CommonLabelAndValueList
          label='Videos:'
        >
          {renderUploaderForm('video', renderVideoMedia, media.length >= MAX_UPLOADED_MEDIA_COUNT)}
        </CommonLabelAndValueList>

        <Checkbox
          label='I affirm that the individuals in these photographs/videos consented to having their image shared with Microsoft for the purpose of being featured in the award winner recognition segment of Microsoft Inspire.'
          checked={!!questionnaire.mediaConsentDisclaimer}
          disabled={!isEditMode}
          onChange={(_, data) => {
            questionnaire.mediaConsentDisclaimer = data.checked as boolean;
          }}
        />
      </FlexBox>
    </>
  );
}

export const WinnerDetailsUploadedMedia = observer(WinnerDetailsUploadedMediaImpl);
